import React from "react";
import { Box, Card } from "@mui/joy";
import type {
  Excursion,
  ExcursionPlanningCardContext,
  ExcursionTemplateLimitsItem,
} from "api/excursions/Excursion.typed";
import { roundToHalfHour, HALF_HOUR } from "../../shared/utils/planningUtils";
import { EXCURSION_CARD_FALLBACK_COLOR } from "utils/colorPalette";
import ExcursionCardContent from "./ExcursionCardContent";
import ExcursionPlanningCardPopover from "./ExcursionPlanningCardPopover";
import ExcursionDropPendingOverlay from "../../../shared/excursionModal/display/ExcursionDropPendingOverlay";
import { excursionCardGlassBorder } from "styles/globalStyles";
import {
  createPlanningExcursionCardClickHandler,
  usePlanningGroupsDrawerOpenSchedule,
} from "../hooks/usePlanningGroupsDrawerOpenSchedule";

export type ExcursionSlotContentProps = ExcursionPlanningCardContext & {
  day: Date;
  hour: number;
  slotExcursions: Excursion[];
  openModalForSlot: (day: Date, startMinutes: number, endMinutes: number) => void;
  onExcursionDrop: (
    day: Date,
    startMinutes: number,
    endMinutes: number,
    excursionId: number,
  ) => void;
  excursionTemplatesList?: ExcursionTemplateLimitsItem[];
};

export default function ExcursionSlotContent(props: ExcursionSlotContentProps) {
  const {
    day,
    hour,
    slotExcursions,
    openModalForSlot,
    onExcursionDrop,
    ...planningPopoverContext
  } = props;
  const {
    canEdit,
    canOpenExcursionDrawer,
    openPlanningGroupsDrawer,
    makeExcursionCardDragHandlers,
    makeExcursionCardDropHandlers,
    onDragEnterCard,
    onDragLeaveCard,
    excursionTemplatesList,
    chipDropPending,
    onOpenExcursionFullModal,
  } = planningPopoverContext;

  const { scheduleOpenDrawer, cancelScheduledDrawer } = usePlanningGroupsDrawerOpenSchedule();

  const handleSlotDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/json");
    if (!raw || !canEdit) return;
    try {
      const d = JSON.parse(raw) as { type: string; excursionId?: number; durationMinutes?: number };
      if (
        d.type !== "excursion" ||
        typeof d.excursionId !== "number" ||
        typeof d.durationMinutes !== "number"
      )
        return;
      const startMinutes = hour * 60;
      const durationRounded = roundToHalfHour(Math.max(HALF_HOUR, d.durationMinutes));
      const endMinutes = startMinutes + durationRounded;
      onExcursionDrop(day, startMinutes, endMinutes, d.excursionId);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box
      onDoubleClick={
        canEdit
          ? (e: React.MouseEvent) => {
              const el = e.target as HTMLElement;
              if (el.closest?.("[data-excursion-card]")) return;
              openModalForSlot(day, hour * 60, (hour + 1) * 60);
            }
          : undefined
      }
      onDragOver={
        canEdit
          ? (e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
            }
          : undefined
      }
      onDrop={canEdit ? handleSlotDrop : undefined}
      data-slot-content
      sx={{
        minHeight: 36,
        width: "100%",
        display: "flex",
        flexWrap: "wrap",
        gap: 0.25,
        alignContent: "flex-start",
        cursor: canEdit || canOpenExcursionDrawer ? "pointer" : undefined,
      }}
    >
      {(slotExcursions ?? []).map((ex) => {
        const cardMuted = ex.status === "cancelled" || ex.status === "validated";
        return (
          <ExcursionPlanningCardPopover key={ex.id} excursion={ex} {...planningPopoverContext}>
            <Card
              data-excursion-card
              size="sm"
              component="div"
              {...makeExcursionCardDragHandlers(ex)}
              {...makeExcursionCardDropHandlers(ex.id)}
              onClick={createPlanningExcursionCardClickHandler({
                canOpenExcursionDrawer,
                canEdit,
                onOpenExcursionFullModal,
                openPlanningGroupsDrawer,
                scheduleOpenDrawer,
                cancelScheduledDrawer,
                excursion: ex,
              })}
              onDragEnter={
                canEdit
                  ? (e: React.DragEvent) => {
                      e.stopPropagation();
                      onDragEnterCard(ex.id);
                    }
                  : undefined
              }
              onDragLeave={
                canEdit
                  ? (e: React.DragEvent) => {
                      e.stopPropagation();
                      onDragLeaveCard(ex.id, e);
                    }
                  : undefined
              }
              sx={{
                position: "relative",
                backgroundColor: ex.color ?? EXCURSION_CARD_FALLBACK_COLOR,
                p: 0.35,
                minHeight: "auto",
                border: excursionCardGlassBorder,
                borderRadius: "8px",
                cursor: canEdit || canOpenExcursionDrawer ? "pointer" : "default",
                textAlign: "left",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                opacity: cardMuted ? 0.5 : 1,
                transition: "box-shadow 0.2s ease",
                boxShadow: "0 1px 6px rgba(0, 0, 0, 0.16)",
                "&:hover": { boxShadow: "0 3px 12px rgba(0, 0, 0, 0.22)" },
              }}
            >
              <ExcursionCardContent
                excursion={ex}
                layout="slot"
                excursionTemplatesList={excursionTemplatesList}
              />
              <ExcursionDropPendingOverlay
                show={chipDropPending?.excursionId === ex.id && chipDropPending.surface === "card"}
              />
            </Card>
          </ExcursionPlanningCardPopover>
        );
      })}
    </Box>
  );
}
